import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Spin, Alert, Divider, Row, Col, List, message } from 'antd';
import axios from 'axios';


const UNITS = ['g', 'ml', 'cup', 'tbsp', 'tsp', 'item'];

const MethodStepInlineCreator = ({ recipeId, onNewStep, initialStep, onCancelEdit }) => {
  const [form] = Form.useForm();

  const [analysed, setAnalysed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [foodsQuery, setFoodsQuery] = useState('');
  const [foodsResult, setFoodsResult] = useState([]);
  const [addedFoods, setAddedFoods] = useState([]);
  const [selectedFood, setSelectedFood] = useState(null);
  const [foodQty, setFoodQty] = useState(0);
  const [foodUnit, setFoodUnit] = useState('');

  useEffect(() => {
    if (initialStep) {
      form.setFieldsValue({
        description: initialStep.recipeMethod.description,
      });

      setAddedFoods(
        initialStep.ingredients.map((i, idx) =>
          i.methodIngredient
            ? i
            : {
                food: i.food,
                methodIngredient: {
                  quantity: i.quantity,
                  quantityType: i.unit,
                  foodId: i.food?.id,
                  order: idx,
                },
              }
        )
      );
    } else {
      form.resetFields();
      setAddedFoods([]);
    }
  }, [initialStep, form]);

  const stepText = Form.useWatch('description', form);

  const handleAnalyse = async () => {
    setLoading(true);
    setAnalysed(null);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/recipes/method/analyse`,
        { description: stepText, recipeId: recipeId },
        { withCredentials: true }
      );
      setAnalysed(data);
    } catch (err) {
      message.error('Analyse failed.');
    }
    setLoading(false);
  };

  const handleAccept = () => {
    form.setFieldsValue({ description: analysed.suggestedDescription });

    setAddedFoods(
      (analysed.ingredients || []).map((item, idx) => ({
        food: item.food || { id: `unknown-${idx}`, name: item.name || 'Unknown' },
        methodIngredient: {
          quantity: item.quantity,
          quantityType: item.unit,
          foodId: item.food?.id || null,
          order: idx,
        },
      }))
    );
    setAnalysed(null);
  };

  const handleSearchFoods = async () => {
    if (!foodsQuery) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/foods/search?q=${foodsQuery}`,
        { withCredentials: true }
      );
      setFoodsResult(data);
    } catch (err) {
      message.error('Food search failed.');
    }
    setLoading(false);
  };

  const handleAddFood = () => {
    if (!selectedFood || !foodQty || !foodUnit) return;
    setAddedFoods([
      ...addedFoods,
      {
        food: selectedFood,
        methodIngredient: {
          quantity: Number(foodQty),
          quantityType: foodUnit,
          methodId: initialStep?.recipeMethod?.id,
          foodId: selectedFood.id,
          order: addedFoods.length,
        },
      },
    ]);
    setSelectedFood(null);
    setFoodQty(0);
    setFoodUnit('');
    setFoodsResult([]);
    setFoodsQuery('');
  };

  const handleSaveStep = () => {
    const values = form.getFieldsValue(true);

    let out;
    if (initialStep && initialStep.recipeMethod) {
      out = {
        recipeMethod: {
          description: values.description,
          id: initialStep.recipeMethod.id,
          order: initialStep.recipeMethod.order,
          recipeId: recipeId,
        },
        ingredients: addedFoods,
      };
    } else {
      out = {
        recipeMethod: { description: values.description, recipeId: recipeId },
        ingredients: addedFoods,
      };
    }
    onNewStep(out);
    form.resetFields();
    setAddedFoods([]);
    message.success('Method step added.');
  };

  return (
    <div style={{ border: '1px solid #eee', padding: 16, marginBottom: 24, borderRadius: 8 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSaveStep}
      >
        <Form.Item
          name="description"
          label="Describe method step"
          rules={[{ required: true, message: 'Please enter the method description' }]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Describe this step (e.g., 'Mix flour and sugar...')"
            disabled={Boolean(analysed)}
          />
        </Form.Item>

        {!analysed && (
          <Button type="primary" onClick={handleAnalyse} loading={loading} disabled={!stepText}>
            Analyse & Suggest
          </Button>
        )}

        {analysed && (
          <>
            <Divider>Suggestion</Divider>
            <Alert
              type="info"
              showIcon
              message={analysed.suggestedDescription}
              description={
                analysed.ingredients?.length > 0 && (
                  <List
                    header="Suggested Foods"
                    dataSource={analysed.ingredients}
                    renderItem={item => (
                      <List.Item>
                        {item.quantity} {item.unit} {item.name}
                        {item.food && ` [Matched: ${item.food.name}]`}
                      </List.Item>
                    )}
                  />
                )
              }
            />
            <Button type="link" onClick={handleAccept} style={{ marginTop: 16 }}>
              Accept suggestion and edit foods
            </Button>
            <Button type="link" onClick={() => setAnalysed(null)}>
              Decline and edit manually
            </Button>
            <Button danger style={{ marginLeft: 8 }} onClick={() => onCancelEdit && onCancelEdit()}>
              Cancel
            </Button>
          </>
        )}

        {((addedFoods.length > 0 && !analysed) || (!analysed && stepText)) && (
          <>
            <Divider>Foods in This Step</Divider>
            <List
              dataSource={addedFoods}
              renderItem={(item, idx) => (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      danger
                      onClick={() => setAddedFoods(addedFoods.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </Button>,
                  ]}
                >
                  {item.methodIngredient.quantity} {item.methodIngredient.quantityType} {item.food.name}
                </List.Item>
              )}
            />

            <Row gutter={8} style={{ marginBottom: 8 }}>
              <Col span={10}>
                <Input
                  value={foodsQuery}
                  placeholder="Search food..."
                  onChange={e => setFoodsQuery(e.target.value)}
                />
              </Col>
              <Col span={4}>
                <Button onClick={handleSearchFoods} style={{ width: '100%' }}>
                  Search
                </Button>
              </Col>
              <Col span={10}>
                {foodsResult.length > 0 && (
                  <Select
                    style={{ width: '100%' }}
                    value={selectedFood?.id}
                    placeholder="Choose food"
                    onChange={id => setSelectedFood(foodsResult.find(f => f.id === id))}
                    options={foodsResult.map(f => ({ label: f.name, value: f.id }))}
                  />
                )}
              </Col>
            </Row>

            <Row gutter={8}>
              <Col span={10}>
                <Input
                  value={foodQty}
                  placeholder="Quantity"
                  type="number"
                  onChange={e => setFoodQty(e.target.value)}
                />
              </Col>
              <Col span={10}>
                <Select
                  value={foodUnit}
                  placeholder="Unit"
                  style={{ width: '100%' }}
                  onChange={setFoodUnit}
                  options={UNITS.map(u => ({ label: u, value: u }))}
                />
              </Col>
              <Col span={4}>
                <Button
                  type="primary"
                  onClick={handleAddFood}
                  disabled={!selectedFood || !foodQty || !foodUnit}
                  style={{ width: '100%' }}
                >
                  Add
                </Button>
              </Col>
            </Row>
          </>
        )}

        {(addedFoods.length > 0 || (!analysed && stepText)) && (
          <Button type="primary" style={{ marginTop: 24 }} htmlType="submit">
            Save Step
          </Button>
        )}
      </Form>
    </div>
  );
};

export default MethodStepInlineCreator;
